import React, {FC, useState} from "react";
import {ISuccessProps, ISummaryProps} from "../types";
import apiService from "../services/api-service";
import {useNavigate} from "react-router-dom";
import {format, parseISO} from "date-fns";
import SelectedParticipantAdditionalServicesModal from "./SummaryAdditionalServicesModal";
import {AxiosResponse} from "axios";
import Spinner from "./Spinner";

interface FormSummaryProps {
    summaryProps: ISummaryProps;
}

export const FormSummary: FC<FormSummaryProps> = ({summaryProps}) => {
    const [isAgreed, setIsAgreed] = useState(false);
    const [isSending, setIsSending] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedParticipantServices, setSelectedParticipantServices] =
        useState<string[]>([]);
    const navigate = useNavigate();

    const handleSendButtonClick = async () => {
        setIsSending(true);
        try {
            let response: AxiosResponse<ISuccessProps, ISuccessProps>;

            if (!summaryProps.organization.leaderName) {
                const participant = {
                    ...summaryProps.participants[0],
                    eventId: summaryProps.eventId,
                    feeTypeId: summaryProps.feeTypeId,
                    accommodationId: summaryProps.participants[0].accommodationId,
                };
                response = await apiService.registerSingleParticipant<ISuccessProps>(participant);
            } else {
                const participants = summaryProps.participants.map((participant) => ({
                    ...participant,
                    additionalServicesIds: participant.additionalServicesIds ?? [],
                }));

                response = await apiService.registerOrganizationWithParticipants<ISuccessProps>({
                    organization: summaryProps.organization,
                    participants,
                    eventId: summaryProps.eventId,
                    feeTypeId: summaryProps.feeTypeId,
                    accommodationId: summaryProps.organization.accommodationId,
                    customAccommodationName: summaryProps.organization.customAccommodationName,
                    customAccommodationAddress: summaryProps.organization.customAccommodationAddress,
                });
            }

            if (response.status === 200) {
                // Wait for the UI update to apply the "disabled" state before navigating
                setTimeout(() => {
                    navigate("/success", {
                        state: {success: response.data},
                    });
                }, 0);
            }
        } catch (error) {
            console.error("Error submitting form:", error);
            setIsSending(false);
        }
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsAgreed(e.target.checked);
    };

    const organizationIsNotEmpty =
        Object.keys(summaryProps.organization).length > 0;

    const openModalWithSelectedParticipantAdditionalServices = (
        services: string[]
    ) => {
        setSelectedParticipantServices(services);
        setIsModalOpen(true);
    };

    return (
        <>
            <div className="p-8 bg-gray-100 min-h-screen">
                <div className="max-w-2xl mx-auto bg-white p-6 shadow-lg rounded-lg">
                    <h1 className="text-2xl font-bold mb-4 text-center text-blue-600">
                        Závazné shrnutí registrace
                    </h1>
                    <div className="mb-8">
                        <h2 className="text-xl font-semibold mb-2">Podrobnosti</h2>
                        <p>
                            <span className="font-semibold">Akce:</span>{" "}
                            {summaryProps.eventName}
                        </p>
                    </div>
                    {/* Event and Organization Details */}
                    {summaryProps.organization.leaderName && (
                        <div className="mb-8">
                            <h2 className="text-xl font-semibold mt-6 mb-2">Organizace</h2>
                            <p>
                                <span className="font-semibold">Jméno:</span>{" "}
                                {summaryProps.organization.organizationName}
                            </p>
                            <p>
                                <span className="font-semibold">Adresa:</span>{" "}
                                {summaryProps.organization.address}
                            </p>
                            <p>
                                <span className="font-semibold">Vedoucí:</span>{" "}
                                {summaryProps.organization.leaderName}{" "}
                                {summaryProps.organization.leaderLastname}
                            </p>
                            <p>
                                <span className="font-semibold">Email:</span>{" "}
                                {summaryProps.organization.email}
                            </p>
                            <p>
                                <span className="font-semibold">Telefonní číslo:</span>{" "}
                                {summaryProps.organization.phoneNumber}
                            </p>
                            <p>
                                <span className="font-semibold">Druh ubytování:</span>{" "}
                                {summaryProps.organization.accommodationTypeName || "N/A"}
                            </p>
                        </div>
                    )}

                    {/* Participants List */}
                    <div>
                        <h2 className="text-xl font-semibold mb-4">Účastníci</h2>
                        {summaryProps.participants.map((participant, index) => {
                            const selectedAdditionalServiceNames =
                                Array.isArray(participant.additionalServices) &&
                                Array.isArray(participant.additionalServicesIds)
                                    ? participant.additionalServices
                                        .filter((service) =>
                                            (participant.additionalServicesIds as string[])!.includes(
                                                service.id
                                            )
                                        )
                                        .map((service) => service.name)
                                    : [];

                            return (
                                <div key={index} className="border-t border-gray-200 pt-4">
                                    <p>
                                        <span className="font-semibold">Jméno a příjmení:</span>{" "}
                                        {participant.name} {participant.lastName}
                                    </p>
                                    <p>
                                        <span className="font-semibold">Email:</span>{" "}
                                        {participant.email}
                                    </p>
                                    <p>
                                        <span className="font-semibold">Druh poplatku:</span>{" "}
                                        {participant.feeTypeName}
                                    </p>
                                    {!summaryProps.organization.accommodationTypeName && (
                                        <p>
                                            <span className="font-semibold">Druh ubytování:</span>{" "}
                                            {participant.accommodationTypeName || "N/A"}
                                        </p>
                                    )}
                                    <p>
                                        <span className="font-semibold">Datum pobytu:</span>{" "}
                                        {participant.stayDates
                                            ?.map((date) =>
                                                date ? format(parseISO(date), "dd.MM.yyyy") : ""
                                            )
                                            .join(", ")}
                                    </p>
                                    <p>
                                        <span className="font-semibold">Doplňkové služby:</span>{" "}
                                        {selectedAdditionalServiceNames.length > 0 ? (
                                            organizationIsNotEmpty ? (
                                                <button
                                                    onClick={() =>
                                                        openModalWithSelectedParticipantAdditionalServices(
                                                            selectedAdditionalServiceNames
                                                        )
                                                    }
                                                    className="text-blue-600"
                                                >
                                                    zobrazit
                                                </button>
                                            ) : (
                                                <span>{selectedAdditionalServiceNames.join(", ")}</span>
                                            )
                                        ) : (
                                            <span>žádné</span>
                                        )}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Centering the checkbox and button */}
                <div className="flex flex-col items-center mt-8">
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            id="agreement"
                            checked={isAgreed}
                            onChange={handleCheckboxChange}
                            className="mr-2"
                        />
                        <label className="text-sm">
                            Souhlasím se zpracováním osobních údajů
                        </label>
                    </div>

                    <button
                        className={`mt-4 p-4 flex items-center justify-center gap-2 text-white font-semibold rounded-lg ${
                            isAgreed && !isSending
                                ? "bg-blue-600 hover:bg-blue-700"
                                : "bg-gray-400 cursor-not-allowed"
                        }`}
                        disabled={!isAgreed || isSending}
                        onClick={handleSendButtonClick}
                    >
                        {isSending && <Spinner size={20}/>}
                        <span className="leading-none">
              {isSending ? "Odesílání..." : "Odeslat"}
            </span>
                    </button>
                </div>
            </div>

            <SelectedParticipantAdditionalServicesModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                services={selectedParticipantServices}
            />
        </>
    );
};
